/* Mybe.GG v25.4 — server/cart-drawer consistency guard.
   /cart.js is authoritative; HTML cached when returning from checkout is not.
   This controller checks the actual line keys and quantities before displaying
   an empty drawer, without intercepting Shopify mutations or checkout forms.
*/
(() => {
  if (window.MybeCartIntegrity) return;

  const ROOT = window.Shopify?.routes?.root || '/';
  let inProgress = null;
  let timer = null;
  let lastValidatedAt = 0;
  let lastVisibilityCheckAt = 0;
  let needsAnotherPass = false;
  const isFee = item => {
    const title = String(item.product_title || item.title || '').toLowerCase();
    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    return title.includes('payment fee') || handle.includes('payment-fee');
  };
  const realItems = cart => (cart?.items || []).filter(item => !isFee(item));
  const count = cart => realItems(cart).reduce((n,item) => n + Number(item.quantity || 0), 0);
  const drawerElement = () => document.querySelector('cart-drawer#CartDrawer[data-section-id]');
  const busy = () => Boolean(
    window.MybeCartClearing || window.MybeDrawerQuantityWorking ||
    window.MybeCardQuickQty?.isBusy?.() ||
    [...document.querySelectorAll('cart-items')].some(item =>
      item.quantityQueueRunning || (item.pendingQuantityUpdates?.size || 0) > 0)
  );

  function matches(cart, panel) {
    if (!panel || !Array.isArray(cart?.items)) return false;
    const expected = realItems(cart);
    const rows = [...panel.querySelectorAll('cart-items .horizontal-products > .horizontal-product')];
    if (rows.length !== expected.length) return false;
    if (!expected.every(item => {
      const row = rows.find(element => element.id === `CartDrawer-Item-${item.key}`);
      const input = row?.querySelector('input[name="updates[]"]');
      return input && Number(input.value) === Number(item.quantity);
    })) return false;

    const empty = panel.querySelector('.drawer__empty');
    const list = panel.querySelector('cart-items')?.closest('.drawer__scrollable');
    const footer = panel.querySelector('.drawer__footer');
    if (expected.length) {
      // Detect the exact reported bug: 4 in the badge but empty-state visible.
      return !empty && !!list && !list.classList.contains('hidden') &&
        !!footer && !footer.hidden && !footer.classList.contains('hidden');
    }
    return !!empty && (!footer || footer.hidden || footer.classList.contains('hidden'));
  }

  function status(drawer, state) {
    if (!drawer) return;
    const panel = drawer.querySelector(`#MiniCart-${drawer.dataset.sectionId}`);
    if (!panel) return;
    let element = panel.querySelector('[data-mybe-cart-integrity-status]');
    drawer.classList.toggle('mybe-cart-restoring', state === 'loading' || state === 'error');
    if (!state) {
      element?.remove();
      return;
    }
    if (!element) {
      element = document.createElement('div');
      element.setAttribute('data-mybe-cart-integrity-status','');
      element.setAttribute('role','status');
      element.setAttribute('aria-live','polite');
      panel.appendChild(element);
    }
    // Safe fixed strings: never interpolate product/user input into HTML.
    element.innerHTML = state === 'loading'
      ? '<span class="mybe-cart-restoring__spinner" aria-hidden="true"></span><span>Updating your cart…</span>'
      : '<span>Couldn’t load your cart items.</span><button type="button" data-mybe-cart-retry>Try again</button>';
  }

  async function getCart(useSharedInitial = false) {
    if (useSharedInitial && window.MybeInitialCart?.load) {
      const cart = await window.MybeInitialCart.load();
      if (!Array.isArray(cart?.items)) throw new Error('Invalid cart data');
      return cart;
    }
    const response = await fetch(`${ROOT}cart.js`, {
      headers: {Accept:'application/json'}, cache:'no-store'
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    const cart = await response.json();
    if (!Array.isArray(cart?.items)) throw new Error('Invalid cart data');
    return cart;
  }

  function synchronizeDisplays(cart) {
    if (busy()) return;
    if (window.MybeCartWriteQueue) window.MybeCartWriteQueue.lastCart = cart;
    // Product cards, floating total and checkout buttons use the same snapshot.
    window.MybeCardQuickQty?.refresh?.(cart);
    window.MybeCartLiveSync?.updateCounters?.(count(cart));
    document.dispatchEvent(new CustomEvent('mybe:cart-confirmed', {
      detail:{cart, direction:'', source:'mybe-cart-integrity'}
    }));
  }

  async function reconcile(options = {}) {
    const drawer = drawerElement();
    if (!drawer) return false;
    if (busy()) {
      schedule(250, {fresh:true});
      return false;
    }
    if (inProgress) {
      needsAnotherPass = true;
      return inProgress;
    }
    inProgress = (async () => {
      try {
        const cart = await getCart(!!options.useSharedInitial);
        if (busy()) { schedule(300,{fresh:true}); return false; }
        // No network/HTML writes take place while a quantity mutation is active.
        synchronizeDisplays(cart);
        lastValidatedAt = Date.now();
        const panel = drawer.querySelector(`#MiniCart-${drawer.dataset.sectionId}`);
        if (matches(cart,panel)) { status(drawer,null); return true; }

        // The drawer's existing onCartRefresh owns the server-rendered markup,
        // native event handlers, footer and checkout controls. Reuse it rather
        // than hand-constructing product HTML from /cart.js.
        status(drawer,'loading');
        for (let attempt = 0; attempt < 2; attempt++) {
          if (busy()) { status(drawer,null); schedule(250,{fresh:true}); return false; }
          const success = await drawer.onCartRefresh?.({detail:{open:false,expectedCart:cart}});
          if (busy()) { status(drawer,null); schedule(250,{fresh:true}); return false; }
          const current = drawer.querySelector(`#MiniCart-${drawer.dataset.sectionId}`);
          if (success && matches(cart,current)) {
            status(drawer,null);
            // The server footer may have been replaced; rebind its checkout guard.
            window.MybeCheckoutSync?.reapply?.();
            return true;
          }
          if (attempt === 0) await new Promise(resolve => setTimeout(resolve,120));
        }
        status(drawer,'error');
        return false;
      } catch (error) {
        console.warn('[Mybe cart integrity]',error);
        // A transient network failure must not block an already consistent
        // populated drawer. Only expose Retry when the display is visibly stale.
        const panel = drawer.querySelector(`#MiniCart-${drawer.dataset.sectionId}`);
        const cached = window.MybeCartWriteQueue?.lastCart;
        const visibleRows = panel?.querySelectorAll('cart-items .horizontal-product').length || 0;
        const displayedCount = Number(drawer.querySelector('.drawer__header cart-count')?.textContent || 0);
        if ((Array.isArray(cached?.items) && matches(cached,panel)) ||
            (visibleRows > 0 && !(Array.isArray(cached?.items) && count(cached) === 0)) ||
            (visibleRows === 0 && displayedCount === 0)) {
          status(drawer,null);
        } else {
          status(drawer,'error');
        }
        return false;
      }
    })();
    try { return await inProgress; }
    finally {
      inProgress = null;
      if (needsAnotherPass) {
        needsAnotherPass = false;
        schedule(120,{fresh:true});
      }
    }
  }

  function schedule(delay = 0, options = {}) {
    clearTimeout(timer);
    timer = setTimeout(() => { timer = null; reconcile(options); },delay);
  }

  // All navigation types: a fresh return (normal navigation) or a bfcache restore.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => schedule(0,{useSharedInitial:true}),{once:true});
  } else schedule(0,{useSharedInitial:true});

  window.addEventListener('pageshow', event => {
    if (event.persisted) schedule(150,{fresh:true});
  });

  // Recheck when the drawer is opened: checkout can change the cart in another tab.
  document.addEventListener('toggle', event => {
    if (event.target?.id === 'CartDrawer' && event.target.hasAttribute('open')) {
      schedule(0,{fresh:true});
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' &&
      Date.now() - lastVisibilityCheckAt > 3000 && Date.now() - lastValidatedAt > 2500) {
      lastVisibilityCheckAt = Date.now();
      schedule(100,{fresh:true});
    }
  });

  // If a Shopify app or alternate product form updates without bundled sections,
  // verify the rendered drawer after its own event handlers have finished.
  const verifyAfterMutation = cart => {
    const drawer = drawerElement();
    if (!drawer) return;
    const panel = drawer.querySelector(`#MiniCart-${drawer.dataset.sectionId}`);
    // Skip unnecessary network reads when Shopify's bundled HTML already has
    // the right line keys and quantities. This is the fast path for rapid taps.
    if (!busy() && Array.isArray(cart?.items) && matches(cart,panel)) return;
    schedule(320,{fresh:true});
  };
  document.addEventListener('cart:updated', event => verifyAfterMutation(event.detail?.cart));
  document.addEventListener('mybe:cart-confirmed', event => {
    if (event.detail?.source !== 'mybe-cart-integrity') verifyAfterMutation(event.detail?.cart);
  });
  document.addEventListener('click', event => {
    if (!event.target.closest?.('[data-mybe-cart-retry]')) return;
    event.preventDefault();
    schedule(0,{fresh:true});
  });

  window.MybeCartIntegrity = {reconcile, matches, schedule};
})();
